import { useState, useEffect, useRef } from "react";
import { useAdminLogin, useAdminRecoverySend, useAdminRecoveryVerify } from "../hooks/useApi";
import "../admin-theme.css";

const DIGIT_MAP = "۰۱۲۳۴۵۶۷۸۹٠١٢٣٤٥٦٧٨٩";
const LATIN = "01234567890123456789";
function normalizeDigits(v: string): string {
  let out = "";
  for (const ch of v) {
    const idx = DIGIT_MAP.indexOf(ch);
    out += idx !== -1 ? LATIN[idx % 10] : ch;
  }
  return out;
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const login = useAdminLogin();

  // ── Recovery (فراموشی رمز عبور) state ──
  const [isRecovery, setIsRecovery] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState<"identifier" | "code">("identifier");
  const [identifier, setIdentifier] = useState("");
  const [identifierError, setIdentifierError] = useState("");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [recoveryChannel, setRecoveryChannel] = useState<"sms" | "email" | null>(null);
  const [normalizedIdentifier, setNormalizedIdentifier] = useState("");
  const timerRef = useRef<number | null>(null);
  const recoverySend = useAdminRecoverySend();
  const recoveryVerify = useAdminRecoveryVerify();

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0 && timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [cooldown]);

  const startCooldown = (sec: number) => {
    setCooldown(sec);
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000) as unknown as number;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate({ email, password });
  };

  const validateIdentifier = (raw: string): string | null => {
    const t = raw.trim();
    if (!t) return "ایمیل یا شماره موبایل را وارد کنید";
    if (t.includes("@")) {
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(t)) return "فرمت ایمیل نامعتبر است";
      return null;
    }
    const digits = normalizeDigits(t).replace(/[^0-9]/g, "");
    if (!/^09\d{9}$/.test(digits)) return "شماره موبایل باید ۱۱ رقم و با 09 شروع شود یا یک ایمیل معتبر وارد کنید";
    return null;
  };

  const handleRecoverySend = (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateIdentifier(identifier);
    if (err) {
      setIdentifierError(err);
      return;
    }
    setIdentifierError("");
    const raw = identifier.trim();
    recoverySend.mutate(
      { identifier: raw },
      {
        onSuccess: (data: Record<string, unknown>) => {
          const channel = (data.channel as string) || (raw.includes("@") ? "email" : "sms");
          setRecoveryChannel(channel as "sms" | "email");
          setNormalizedIdentifier((data.identifier as string) || raw);
          setRecoveryStep("code");
          setCode("");
          const expires = (data.expires_in as number) || 120;
          startCooldown(expires);
        },
      }
    );
  };

  const handleRecoveryVerify = (e: React.FormEvent) => {
    e.preventDefault();
    const codeDigits = normalizeDigits(code).replace(/[^0-9]/g, "").slice(0, 5);
    if (codeDigits.length !== 5) return;
    recoveryVerify.mutate({ identifier: normalizedIdentifier || identifier.trim(), code: codeDigits });
  };

  const handleRecoveryResend = () => {
    if (cooldown > 0) return;
    const raw = (normalizedIdentifier || identifier.trim());
    recoverySend.mutate(
      { identifier: raw },
      {
        onSuccess: (data: Record<string, unknown>) => {
          const expires = (data.expires_in as number) || 120;
          startCooldown(expires);
        },
      }
    );
  };

  const handleToggleRecovery = () => {
    if (isRecovery) {
      // closing recovery
      setIsRecovery(false);
      setRecoveryStep("identifier");
      setIdentifier("");
      setIdentifierError("");
      setCode("");
      setCooldown(0);
      setRecoveryChannel(null);
      setNormalizedIdentifier("");
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    } else {
      setIsRecovery(true);
    }
  };

  const handleEditIdentifier = () => {
    setRecoveryStep("identifier");
    setCode("");
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setCooldown(0);
  };

  const handleIdentifierChange = (v: string) => {
    setIdentifier(v);
    if (identifierError) setIdentifierError("");
  };

  const handleCodeChange = (v: string) => {
    const digits = normalizeDigits(v).replace(/[^0-9]/g, "").slice(0, 5);
    setCode(digits);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* ─── Left Side: Branding / Illustration ─── */}
      <div className="admin-login-bg flex-1 flex items-center justify-center p-8 md:p-12 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-white/5" />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-white/5" />
        <div className="absolute top-1/3 left-1/4 w-32 h-32 rounded-full bg-white/5" />

        <div className="relative z-10 text-center md:text-right max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 justify-center md:justify-start mb-6">
            <img src="/admin-logo-reverse-filled.svg" alt="پنل مدیریت" className="w-12 h-12" />
            <div>
              <h1 className="text-2xl font-bold text-white leading-tight">پنل مدیریت</h1>
              <p className="text-sm text-white/70">AI Gateway</p>
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
            داشبورد مدیریتی
          </h2>
          <p className="text-lg text-white/80 leading-relaxed mb-8">
            مدیریت کاربران، مدل‌ها، تنظیمات سیستم و پشتیبانی در یک پنل یکپارچه
          </p>

          {/* Feature list */}
          <div className="space-y-3 hidden md:block">
            {[
              "مدیریت کاربران و دسترسی‌ها",
              "کنترل مدل‌های هوش مصنوعی",
              "گزارش‌گیری و آمار سیستم",
              "پشتیبانی و مدیریت تیکت‌ها",
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-white/80">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-white/60 shrink-0">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Right Side: Login Form ─── */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "var(--admin-primary)", boxShadow: "0 4px 20px rgba(7, 135, 131, 0.25)" }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                <rect x="3" y="11" width="18" height="11" rx="2" stroke="white" />
                <path d="M7 11V7a5 5 0 0110 0v4" stroke="white" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-1" style={{ color: "var(--admin-text-primary)" }}>ورود مدیر</h1>
            <p className="text-sm" style={{ color: "var(--admin-text-secondary)" }}>برای دسترسی به پنل مدیریت وارد شوید</p>
          </div>

          {!isRecovery ? (
            <>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                    ایمیل
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="admin-input"
                    placeholder="admin@example.com"
                    required
                    dir="ltr"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                    رمز عبور
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="admin-input"
                    placeholder="••••••••"
                    required
                    dir="ltr"
                  />
                </div>

                <button
                  type="submit"
                  disabled={login.isPending}
                  className="admin-btn admin-btn-primary w-full !py-3 text-base"
                >
                  {login.isPending ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      در حال ورود...
                    </span>
                  ) : (
                    "ورود به پنل مدیریت"
                  )}
                </button>
              </form>

              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={handleToggleRecovery}
                  className="text-sm font-medium hover:underline"
                  style={{ color: "var(--admin-primary)" }}
                >
                  فراموشی رمز عبور
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-5">
              {recoveryStep === "identifier" ? (
                <form onSubmit={handleRecoverySend} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                      ایمیل یا شماره موبایل
                    </label>
                    <input
                      type="text"
                      value={identifier}
                      onChange={(e) => handleIdentifierChange(e.target.value)}
                      className="admin-input"
                      placeholder="admin@example.com یا 09xxxxxxxxx"
                      dir="ltr"
                      autoComplete="username"
                      autoFocus
                      required
                      style={identifierError ? { borderColor: "var(--admin-error)", boxShadow: "0 0 0 3px rgba(220,38,38,0.12)" } : undefined}
                    />
                    {identifierError ? (
                      <p className="text-xs mt-1.5" style={{ color: "var(--admin-error)" }}>{identifierError}</p>
                    ) : (
                      <p className="text-xs mt-1.5" style={{ color: "var(--admin-text-tertiary)" }}>
                        کد تایید ۵ رقمی به ایمیل یا شماره موبایل شما ارسال خواهد شد
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={recoverySend.isPending}
                    className="admin-btn admin-btn-primary w-full !py-3 text-base"
                  >
                    {recoverySend.isPending ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        در حال ارسال...
                      </span>
                    ) : (
                      "ارسال کد تایید"
                    )}
                  </button>

                  <div className="flex items-center justify-center gap-2">
                    <div className="h-px flex-1" style={{ background: "var(--admin-border)" }} />
                    <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>یا</span>
                    <div className="h-px flex-1" style={{ background: "var(--admin-border)" }} />
                  </div>

                  <button
                    type="button"
                    onClick={handleToggleRecovery}
                    className="admin-btn admin-btn-secondary w-full !py-2.5 text-sm"
                  >
                    بازگشت به ورود با رمز عبور
                  </button>
                </form>
              ) : (
                <form onSubmit={handleRecoveryVerify} className="space-y-5">
                  <div
                    className="rounded-xl px-3.5 py-3 flex items-center justify-between gap-2"
                    style={{ background: "var(--admin-info-bg)", border: "1px solid var(--admin-primary-light)" }}
                  >
                    <span className="text-sm" style={{ color: "var(--admin-text-secondary)" }}>
                      کد به{" "}
                      <span dir="ltr" className="font-medium" style={{ color: "var(--admin-text-primary)" }}>
                        {normalizedIdentifier || identifier}
                      </span>{" "}
                      {recoveryChannel === "email" ? "ایمیل شد" : "ارسال شد"}
                    </span>
                    <button
                      type="button"
                      onClick={handleEditIdentifier}
                      className="text-xs font-medium hover:underline shrink-0"
                      style={{ color: "var(--admin-primary)" }}
                    >
                      ویرایش
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                      کد تایید
                    </label>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => handleCodeChange(e.target.value)}
                      className="admin-input text-center tracking-[0.45em] text-lg font-medium"
                      placeholder="─ ─ ─ ─ ─"
                      dir="ltr"
                      inputMode="numeric"
                      maxLength={5}
                      autoComplete="one-time-code"
                      autoFocus
                      required
                    />
                    <div className="flex items-center justify-between mt-2">
                      {cooldown > 0 ? (
                        <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                          ارسال مجدد تا {cooldown} ثانیه دیگر
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={handleRecoveryResend}
                          disabled={recoverySend.isPending}
                          className="text-xs font-medium hover:underline disabled:opacity-50"
                          style={{ color: "var(--admin-primary)" }}
                        >
                          ارسال مجدد کد
                        </button>
                      )}
                      <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                        {code.length}/5
                      </span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={recoveryVerify.isPending || code.length !== 5}
                    className="admin-btn admin-btn-primary w-full !py-3 text-base"
                  >
                    {recoveryVerify.isPending ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        در حال ورود...
                      </span>
                    ) : (
                      "تایید و ورود"
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleEditIdentifier}
                    className="w-full text-center text-sm hover:underline"
                    style={{ color: "var(--admin-text-tertiary)" }}
                  >
                    بازگشت و ویرایش
                  </button>
                  <button
                    type="button"
                    onClick={handleToggleRecovery}
                    className="admin-btn admin-btn-secondary w-full !py-2.5 text-sm"
                  >
                    بازگشت به ورود با رمز عبور
                  </button>
                </form>
              )}
            </div>
          )}

          <p className="text-center text-xs mt-8" style={{ color: "var(--admin-text-tertiary)" }}>
            تنها مدیران سیستم مجاز به ورود هستند
          </p>
        </div>
      </div>
    </div>
  );
}
